import React, { useState } from 'react';
import { Select, MenuItem } from '@mui/material';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import CloseIcon from '@mui/icons-material/Close';

const UploadButton: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
  <button
    onClick={onClick}
    className="px-6 py-2.5 text-white font-medium rounded-xl transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 flex items-center gap-2"
    style={{ background: 'linear-gradient(to right, #3B82F6, #9333EA)' }}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
    {label}
  </button>
);

const FilterPill: React.FC<{ value: string; label: string; selected: string; onSelect: (v: string) => void }> =
  ({ value, label, selected, onSelect }) => (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`px-4 py-2 rounded-full border transition-all duration-200 flex items-center gap-2 ${
        selected === value ? 'text-white border-transparent' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
      }`}
      style={selected === value ? { background: 'linear-gradient(135deg, #F39A81, #959BEA)' } : {}}
    >
      {selected === value && (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
      )}
      {label}
    </button>
  );

interface VideoFiltersProps {
  sortBy: string;
  selectedStatus: string;
  selectedVideoType?: string;
  onSortChange: (sort: string) => void;
  onStatusChange: (status: string) => void;
  onVideoTypeChange?: (videoType: string) => void;
  showUploadButton?: boolean;
  onUploadClick?: () => void;
  onImageUploadClick?: () => void;
  onTranscriptUploadClick?: () => void;
  hasVideos: boolean;
  pageValue: string;
  onResetFilters?: () => void;
  tabSlot?: React.ReactNode;
  hideControls?: boolean;
}

const VideoFilters: React.FC<VideoFiltersProps> = ({
  sortBy,
  selectedStatus,
  selectedVideoType,
  onSortChange,
  onStatusChange,
  onVideoTypeChange,
  showUploadButton = true,
  onUploadClick,
  onImageUploadClick,
  onTranscriptUploadClick,
  hasVideos,
  pageValue,
  onResetFilters: _onResetFilters,
  tabSlot,
  hideControls = false,
}) => {
  const { isMobile, isTablet } = React.use(DeviceContext) ?? {
    isMobile: false,
    isTablet: false
  };
  return (
    <>
      {/* Filter Controls */}
      <div className={`mb-8 px-4 lg:px-8 flex ${pageValue === 'Transcripts' ? 'justify-center' : 'justify-between'} items-center`}>
        {/* Left side - Filters (hidden for Transcripts or when hideControls) */}
        <div className={`flex flex-row items-center gap-6 ${pageValue === 'Transcripts' ? 'hidden' : 'flex'} w-full`}>
          {/* Filter Controls and Upload Button */}
          {!hideControls && <div className={`flex flex-col sm:flex-row gap-4 items-start sm:items-end ${isMobile || isTablet ? 'w-full' : ''}`}>
            {/* Sort By Dropdown */}
            <div className="min-w-0 flex-1 sm:min-w-[180px]">
              <label htmlFor='sort-by-select' className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                Sort by
              </label>
              <Select
                id='sort-by-select'
                type='select'
                sx={{
                  borderRadius: '10px',
                  fontSize: '14px',
                  width: '100%',
                }}
                size='small'
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="recent">Recent</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="largest">Largest</MenuItem>
                <MenuItem value="smallest">Smallest</MenuItem>
              </Select>
            </div>

            {/* Status Filter Dropdown */}
            <div className="min-w-0 flex-1 sm:min-w-[160px]">
              <label htmlFor='status-select' className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                Status
              </label>
              <Select
                id='status-select'
                type='select'
                value={selectedStatus}
                onChange={(e) => onStatusChange(e.target.value)}
                sx={{
                  borderRadius: '10px',
                  fontSize: '14px',
                  width: '100%',
                  padding: 0,
                }}
                size="small"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </div>

            {/* Video Type Filter Dropdown - only shown for Videos page */}
            {pageValue === 'Videos' && onVideoTypeChange && (
              <div className="min-w-0 flex-1 sm:min-w-[160px]">
                <label htmlFor='video-type-select' className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                  Video Type
                </label>
                <Select
                  id='video-type-select'
                  type='select'
                  value={selectedVideoType || 'all'}
                  onChange={(e) => onVideoTypeChange(e.target.value)}
                  sx={{
                    borderRadius: '10px',
                    fontSize: '14px',
                    width: '100%',
                    padding: 0,
                  }}
                  size="small"
                >
                  <MenuItem value="all">All Videos</MenuItem>
                  <MenuItem value="original">Original</MenuItem>
                  <MenuItem value="extended">Extended</MenuItem>
                </Select>
              </div>
            )}

            {/* Upload Button */}
            {showUploadButton && pageValue === 'Videos' && onUploadClick && (
              <div className="flex items-end">
                <UploadButton onClick={onUploadClick} label="Upload Video" />
              </div>
            )}

            {showUploadButton && pageValue === 'Images' && onImageUploadClick && (
              <div className="flex items-end">
                <UploadButton onClick={onImageUploadClick} label="Upload Image" />
              </div>
            )}
          </div>}
          {/* Tab slot (e.g. My Videos / My Requests toggle) - shown on the right */}
          {tabSlot && (
            <div className="flex-shrink-0 ml-auto">
              {tabSlot}
            </div>
          )}
        </div>

        {/* Transcript Upload Button - shown when on Transcripts tab */}
        {showUploadButton && pageValue === 'Transcripts' && onTranscriptUploadClick && (
          <div className="flex items-end">
            <UploadButton onClick={onTranscriptUploadClick} label="Upload Transcript" />
          </div>
        )}
      </div>

      {/* Upload Button for Empty State */}
        {!hasVideos && showUploadButton && pageValue === 'Videos' && onUploadClick && (
          <div className="mb-8 px-4 lg:px-8 flex justify-center">
            <UploadButton onClick={onUploadClick} label="Upload Your First Video" />
          </div>
        )}
    </>
  );
};

// Mobile Video Filter Component with Drawer
const MobileVideoFilter: React.FC<VideoFiltersProps> = ({
  sortBy,
  selectedStatus,
  selectedVideoType,
  onSortChange,
  onStatusChange,
  onVideoTypeChange,
  showUploadButton = true,
  onUploadClick,
  onImageUploadClick,
  onTranscriptUploadClick,
  hasVideos,
  pageValue,
  onResetFilters,
  tabSlot: _tabSlot,
  hideControls: _hideControls,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [tempSortBy, setTempSortBy] = useState(sortBy);
  const [tempStatus, setTempStatus] = useState(selectedStatus);
  const [tempVideoType, setTempVideoType] = useState(selectedVideoType || 'all');

  const sortOptions = [
    { value: 'recent', label: 'Recent' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'largest', label: 'Largest' },
    { value: 'smallest', label: 'Smallest' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const videoTypeOptions = [
    { value: 'all', label: 'All Videos' },
    { value: 'original', label: 'Original' },
    { value: 'extended', label: 'Extended' },
  ];

  const handleApply = () => {
    onSortChange(tempSortBy);
    onStatusChange(tempStatus);
    if (onVideoTypeChange) {
      onVideoTypeChange(tempVideoType);
    }
    setIsDrawerOpen(false);
  };

  const handleReset = () => {
    setTempSortBy('recent');
    setTempStatus('all');
    setTempVideoType('all');
    if (onResetFilters) {
      onResetFilters();
    }
  };

  const handleClose = () => {
    // Reset temp values to current values when closing without applying
    setTempSortBy(sortBy);
    setTempStatus(selectedStatus);
    setTempVideoType(selectedVideoType || 'all');
    setIsDrawerOpen(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (sortBy !== 'recent') count++;
    if (selectedStatus !== 'all') count++;
    if (pageValue === 'Videos' && selectedVideoType && selectedVideoType !== 'all') count++;
    return count;
  };

  return (
    <>
      {/* Filter Button and Upload Button Row */}
      <div className={`mb-4 px-4 flex items-center ${pageValue === 'Transcripts' ? 'justify-center' : 'justify-between'}`}>
        {/* Filter Button */}
        {pageValue !== 'Transcripts' && (
          <button
            type="button"
            onClick={() => setIsDrawerOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-transparent bg-clip-padding text-gray-700 font-medium transition-all duration-200"
            style={{
              background: "linear-gradient(white, white) padding-box, linear-gradient(135deg, #3B82F6, #9333EA) border-box",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" stroke="#393939" />
            </svg>
            <p className='text-gray-500 font-medium text-xl'>{getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}</p>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 3.77734L9.5 3.77734L6 8.97179L2.5 3.77734Z" fill="#393939" />
            </svg>
          </button>
        )}

        {/* Upload Button */}
        {showUploadButton && pageValue === 'Videos' && onUploadClick && (
          <UploadButton onClick={onUploadClick} label="Upload Video" />
        )}

        {showUploadButton && pageValue === 'Images' && onImageUploadClick && (
          <UploadButton onClick={onImageUploadClick} label="Upload Image" />
        )}

        {showUploadButton && pageValue === 'Transcripts' && onTranscriptUploadClick && (
          <UploadButton onClick={onTranscriptUploadClick} label="Upload Transcript" />
        )}
      </div>

      {/* Upload Button for Empty State */}
      {!hasVideos && showUploadButton && onUploadClick && pageValue === 'Videos' && (
        <div className="mb-8 px-4 flex justify-center">
          <UploadButton onClick={onUploadClick} label="Upload Your First Video" />
        </div>
      )}

      {/* Drawer Overlay */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-50">
          {/* Background Overlay */}
          <button
            type="button"
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            aria-label="Close modal"
            onClick={handleClose}
          />

          {/* Drawer Content */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 transform translate-y-0 transition-transform duration-300 ease-out animate-in slide-in-from-bottom">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">
                Filter & Sort {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
              </h2>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-full transition-colors bg-white"
              >
                <CloseIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Sort By Section */}
            <div className="mb-6">
              <h3 className="text-base font-medium text-gray-900 mb-3">Sort By</h3>
              <div className="flex flex-wrap gap-2">
                {sortOptions.map((option) => (
                  <FilterPill key={option.value} value={option.value} label={option.label} selected={tempSortBy} onSelect={setTempSortBy} />
                ))}
              </div>
            </div>

            {/* Status Section */}
            <div className="mb-8">
              <h3 className="text-base font-medium text-gray-900 mb-3">Status</h3>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((option) => (
                  <FilterPill key={option.value} value={option.value} label={option.label} selected={tempStatus} onSelect={setTempStatus} />
                ))}
              </div>
            </div>

            {/* Video Type Section - only shown for Videos page */}
            {pageValue === 'Videos' && onVideoTypeChange && (
              <div className="mb-8">
                <h3 className="text-base font-medium text-gray-900 mb-3">Video Type</h3>
                <div className="flex flex-wrap gap-2">
                  {videoTypeOptions.map((option) => (
                    <FilterPill key={option.value} value={option.value} label={option.label} selected={tempVideoType} onSelect={setTempVideoType} />
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="flex-1 py-3 px-6 border border-black text-gray-700 font-medium rounded-full bg-white transition-colors"
              >
                Reset
              </button>
              <button
                type='button'
                onClick={handleApply}
                className="flex-1 py-3 px-6 bg-gray-900 text-white font-medium rounded-full hover:bg-gray-800 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export { MobileVideoFilter, VideoFilters };
